import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "髪の毛・細い部分が消える 背景除去ツール比較 — エッジ精度で選ぶ",
	description:
		"背景除去ツールで髪の毛・細かい部分が消える問題をエッジ精度で比較。remove.bg・PhotoRoom・Canva・Adobe Express・しろぬきやの精度を2026年版で解説。",
	publishDate: "2026-03-16",
	slug: "background-removal-edge-precision",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function BackgroundRemovalEdgePrecision() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							背景除去ツールを使ったら「フリンジ（毛並み）が消えた」「ファーの端がガタガタになった」「商品のワイヤー部分が切れた」——こういった経験はエッジ精度の差が原因です。ツール選びで大きく変わります。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							エッジ精度が重要な商品のケース
						</h2>
						<ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
							<li>ぬいぐるみ・ファー素材（毛並みが細かい）</li>
							<li>アクセサリー・ネックレス（チェーンが細い）</li>
							<li>観葉植物（葉の隙間が複雑）</li>
							<li>ガラス製品（透明部分がある）</li>
							<li>メガネのフレーム（細い輪郭）</li>
						</ul>
						<p className="text-gray-700">
							これらの商品は、エッジ精度の低いツールだと細部が欠けたり背景が残ったりします。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							ツール別 エッジ精度比較（2026年版）
						</h2>

						<div className="overflow-x-auto mb-6">
							<table className="w-full text-sm border-collapse">
								<thead>
									<tr className="bg-gray-100">
										<th className="border px-3 py-2 text-left">ツール</th>
										<th className="border px-3 py-2 text-center">
											髪の毛・毛並み
										</th>
										<th className="border px-3 py-2 text-center">細線・金属</th>
										<th className="border px-3 py-2 text-center">
											ガラス・透明
										</th>
										<th className="border px-3 py-2 text-center">
											自動修正機能
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border px-3 py-2 font-semibold">
											remove.bg
										</td>
										<td className="border px-3 py-2 text-center">★★★★★</td>
										<td className="border px-3 py-2 text-center">★★★★</td>
										<td className="border px-3 py-2 text-center">★★★</td>
										<td className="border px-3 py-2 text-center">なし</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border px-3 py-2 font-semibold">
											PhotoRoom
										</td>
										<td className="border px-3 py-2 text-center">★★★★★</td>
										<td className="border px-3 py-2 text-center">★★★★</td>
										<td className="border px-3 py-2 text-center">★★★</td>
										<td className="border px-3 py-2 text-center">あり</td>
									</tr>
									<tr>
										<td className="border px-3 py-2 font-semibold">
											Adobe Express
										</td>
										<td className="border px-3 py-2 text-center">★★★★</td>
										<td className="border px-3 py-2 text-center">★★★★</td>
										<td className="border px-3 py-2 text-center">★★★★</td>
										<td className="border px-3 py-2 text-center">あり</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border px-3 py-2 font-semibold">Canva</td>
										<td className="border px-3 py-2 text-center">★★★</td>
										<td className="border px-3 py-2 text-center">★★★</td>
										<td className="border px-3 py-2 text-center">★★</td>
										<td className="border px-3 py-2 text-center">あり</td>
									</tr>
									<tr>
										<td className="border px-3 py-2 font-semibold">
											しろぬきや
										</td>
										<td className="border px-3 py-2 text-center">★★★★</td>
										<td className="border px-3 py-2 text-center">★★★★</td>
										<td className="border px-3 py-2 text-center">★★★</td>
										<td className="border px-3 py-2 text-center">自動白背景</td>
									</tr>
								</tbody>
							</table>
						</div>

						<h3 className="text-lg font-medium mt-6 mb-3">ツール別の特徴</h3>

						<div className="space-y-4">
							<div>
								<h4 className="font-semibold text-gray-800 mb-1">remove.bg</h4>
								<p className="text-sm text-gray-700">
									ポートレートと商品画像のエッジ処理に特化。巻き毛・細かい毛並みの処理が業界トップレベル。ただし無料版は解像度制限あり・商用不可。
								</p>
							</div>
							<div>
								<h4 className="font-semibold text-gray-800 mb-1">PhotoRoom</h4>
								<p className="text-sm text-gray-700">
									remove.bgと並んでエッジ精度が高い。手動修正ブラシ機能もあり、細部の調整が可能。スマートフォンアプリとしても人気。
								</p>
							</div>
							<div>
								<h4 className="font-semibold text-gray-800 mb-1">
									Adobe Express
								</h4>
								<p className="text-sm text-gray-700">
									ガラス・透明素材の扱いが比較的得意。モーションブラーがかかった被写体でも処理できる。Adobe
									IDが必要。
								</p>
							</div>
							<div>
								<h4 className="font-semibold text-gray-800 mb-1">Canva</h4>
								<p className="text-sm text-gray-700">
									2〜5秒で高速処理。シンプルな商品（衣類・雑貨）は十分な精度。複雑なエッジは手動修正が必要になることがある。
								</p>
							</div>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							精度が低かった場合の対処法
						</h2>
						<ol className="list-decimal list-inside space-y-2 text-gray-700">
							<li>より精度の高いツール（remove.bg / PhotoRoom）で再処理</li>
							<li>GIMPやPhotoshopで手動マスク補正</li>
							<li>商品を白い背景の前で撮り直す（根本解決）</li>
							<li>しろぬきやで処理→精度が不足なら手動マスク補正ツールに渡す</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							しろぬきやはAI背景除去に加えて、EC規格への自動リサイズをセットで行えます。まずしろぬきやで処理し、エッジ精度が足りない部分は手動ツールで補正するフローが効率的です。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
